import { formatPleskBytes } from './hosting-storage-format';
import type { HostingPleskTechnicalView } from '@tosho/contracts';
import { PrototypeIcon } from './PrototypeIcon';
import { siteCreationDate } from './hosting-site-profile';

export function HostingSiteTechnicalDetails({
  value,
  busy,
  disabled,
  onRead,
}: {
  readonly value: HostingPleskTechnicalView | null;
  readonly busy: boolean;
  readonly disabled: boolean;
  readonly onRead: () => void;
}): React.JSX.Element {
  return (
    <section className="hosting-site-technical" aria-label="Informations techniques Plesk">
      <header>
        <div>
          <h3>Informations Plesk</h3>
          <p>
            {value
              ? 'Lecture enregistrée : ' + new Date(value.observedAt).toLocaleString('fr-FR')
              : 'Aucune lecture technique enregistrée.'}
          </p>
        </div>
        <button type="button" disabled={busy || disabled} onClick={onRead}>
          <PrototypeIcon name="refresh-cw" />
          {busy ? 'Lecture…' : 'Lire dans Plesk'}
        </button>
      </header>
      <p className="hosting-site-technical__help">
        Lecture seule. Les bases et le quota concernent l’abonnement partagé ; ils ne sont pas
        attribués automatiquement à ce site.
      </p>
      {value ? (
        <>
          {!value.accessIsCurrent ? (
            <p role="status">
              Ces données sont historiques : renouvelez la lecture pour les confirmer avec l’accès
              actuel.
            </p>
          ) : null}
          <dl>
            <div>
              <dt>Création Plesk</dt>
              <dd>{value.createdOn ? siteCreationDate(value.createdOn) : 'Non retournée'}</dd>
            </div>
            <div>
              <dt>Statut du vhost</dt>
              <dd>
                {value.statusCode === null
                  ? 'Non retourné'
                  : value.statusCode === '0'
                    ? 'Actif lors de la lecture'
                    : 'Restriction signalée · code ' + value.statusCode}
              </dd>
            </div>
            <div>
              <dt>Taille sur HDD</dt>
              <dd>{formatPleskBytes(value.diskUsageBytes)}</dd>
            </div>
            <div>
              <dt>Abonnement</dt>
              <dd>{value.subscription?.name ?? 'Non retourné'}</dd>
            </div>
            <div>
              <dt>Quota disque de l’abonnement</dt>
              <dd>
                {value.subscription?.unlimitedDisk
                  ? 'Illimité'
                  : formatPleskBytes(value.subscription?.diskQuotaBytes ?? null)}
              </dd>
            </div>
            <div>
              <dt>PHP du vhost</dt>
              <dd>
                {value.php?.enabled === true
                  ? 'Activé'
                  : value.php?.enabled === false
                    ? 'Désactivé'
                    : 'Non retourné'}
                {value.php?.handler ? ' · ' + value.php.handler : ''}
              </dd>
            </div>
          </dl>
          <details open>
            <summary>Accès aux fichiers du vhost</summary>
            <dl>
              <div>
                <dt>Adresse IP d’hébergement</dt>
                <dd>
                  {value.transferAccess?.ipAddresses.length
                    ? value.transferAccess.ipAddresses.join(' · ')
                    : 'Non retournée'}
                </dd>
              </div>
              <div>
                <dt>Utilisateur FTP / FTPS</dt>
                <dd>{value.transferAccess?.login ?? 'Non retourné'}</dd>
              </div>
              <div>
                <dt>Compte partagé</dt>
                <dd>
                  {value.transferAccess?.accountScope === 'SUBSCRIPTION'
                    ? 'Compte principal de l’abonnement'
                    : value.transferAccess?.login
                      ? 'Compte retourné pour ce vhost ; il peut être partagé'
                      : 'Non déterminé'}
                </dd>
              </div>
              <div>
                <dt>Accès SSH / SFTP</dt>
                <dd>
                  {value.transferAccess?.sshShell
                    ? [
                        'false',
                        '/bin/false',
                        '/sbin/nologin',
                        '/usr/sbin/nologin',
                        'forbidden',
                      ].includes(value.transferAccess.sshShell)
                      ? 'Shell désactivé dans Plesk'
                      : 'Shell déclaré : ' + value.transferAccess.sshShell
                    : 'Non confirmé par Plesk'}
                </dd>
              </div>
            </dl>
            <p>
              La disponibilité de FTPS et de SFTP et leurs ports dépendent du serveur. Aucun mot de
              passe n’est récupéré.
            </p>
          </details>
          <details open>
            <summary>WordPress détecté par WP Toolkit</summary>
            {value.wordpress ? (
              value.wordpress.installations.length ? (
                <ul>
                  {value.wordpress.installations.map((site) => (
                    <li key={site.url}>
                      {site.url} · WordPress {site.version}
                    </li>
                  ))}
                </ul>
              ) : (
                <p>Aucune installation retournée pour ce vhost.</p>
              )
            ) : (
              <p>Version non retournée par WP Toolkit.</p>
            )}
          </details>
          <details>
            <summary>Paramètres PHP de l’abonnement</summary>
            {value.php?.settings.length ? (
              <dl>
                {value.php.settings.map((s) => (
                  <div key={s.name}>
                    <dt>{s.name}</dt>
                    <dd>{s.value}</dd>
                  </div>
                ))}
              </dl>
            ) : (
              <p>Paramètres non retournés.</p>
            )}
          </details>
          <details>
            <summary>
              Bases de données de l’abonnement
              {value.databases ? ' · ' + value.databases.length : ''}
            </summary>
            {value.databases === null ? (
              <Unavailable />
            ) : value.databases.length ? (
              <ul>
                {value.databases.map((db, i) => (
                  <li key={i}>
                    {db.name} · {db.type}
                    <small>
                      {db.users === null
                        ? 'Comptes non retournés'
                        : db.users.length
                          ? 'Comptes : ' + db.users.join(', ')
                          : 'Aucun compte retourné'}
                    </small>
                  </li>
                ))}
              </ul>
            ) : (
              <p>Aucune base retournée pour cet abonnement.</p>
            )}
            <p>Les comptes sont lus pour un maximum de 20 bases par relevé.</p>
          </details>
          <details>
            <summary>
              Enregistrements DNS du site{value.dns ? ' · ' + value.dns.length : ''}
            </summary>
            {value.dns === null ? (
              <Unavailable />
            ) : value.dns.length ? (
              <div className="hosting-site-technical__dns">
                <table>
                  <caption className="sr-only">DNS retournés par Plesk</caption>
                  <thead>
                    <tr>
                      <th>Type</th>
                      <th>Hôte</th>
                      <th>Valeur</th>
                    </tr>
                  </thead>
                  <tbody>
                    {value.dns.map((record, i) => (
                      <tr key={i}>
                        <td>{record.type}</td>
                        <td>{record.host}</td>
                        <td>
                          {record.value}
                          {record.option ? ' · ' + record.option : ''}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p>Aucun enregistrement retourné.</p>
            )}
          </details>
        </>
      ) : (
        <p>
          Cette lecture demande une connexion Tosho récente et une clé Plesk autorisée pour l’API
          XML.
        </p>
      )}
    </section>
  );
}
function Unavailable(): React.JSX.Element {
  return (
    <p>
      Non retourné par Plesk. Vérifiez les droits de la clé et la disponibilité de cette rubrique.
    </p>
  );
}
export { formatPleskBytes } from './hosting-storage-format';
